export type EventLoggerData = {
	[TKey in string]: string | number | boolean | Array<string | number | boolean | EventLoggerData> | EventLoggerData
}

export type EventLoggerOptions = {
	enabled: boolean
	clientApiEndpoint: string
	opportunityApiEndpoint?: string
	projectId: string
	getToken: () => Awaitable<string>
	getCommonData?: () => EventLoggerData
	shouldLog?: (event: LogEvent) => boolean
	onLog?: (event: LogEvent) => void
	userDataWhitelist?: Array<string>
}

export type EventLogger = {
	log: (type: string, identifier: string, data?: EventLoggerData | null) => void
	sanitize: (userData: EventLoggerData) => EventLoggerData
}

export type LogEvent = {
	type: string
	identifier: string
	project: string
	createdTime: string
	data: EventLoggerData
}

export const eventLoggerCreate = ({
	enabled,
	clientApiEndpoint,
	projectId,
	getToken,
	getCommonData,
	shouldLog,
	onLog,
	userDataWhitelist = [],
}: EventLoggerOptions): EventLogger => {
	const url = `${clientApiEndpoint}/tracking/events`

	const queue: Array<LogEvent> = []
	let drainScheduled = false

	const drainQueue = async (nextDelay = 1) => {
		drainScheduled = true

		const token = await getToken()

		const headers = {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${token}`,
		}

		let event: LogEvent | undefined
		// Read events from the start of the queue
		// biome-ignore lint/suspicious/noAssignInExpressions: Intentional
		while ((event = queue.shift())) {
			try {
				// Ignore response
				await fetch(url, {
					method: 'POST',
					headers,
					body: JSON.stringify(event),
					credentials: 'omit',
				})
			} catch {
				// On error, return the event to the back of the queue and begin exponential falloff
				queue.push(event)
				drainAfter(nextDelay)
				return
			}
		}
		drainScheduled = false
	}

	const drainAfter = (delay: number) => {
		setTimeout(() => {
			drainQueue(Math.max(10, delay * 2))
		}, delay * 1000)
	}

	return {
		log: (type, identifier, data) => {
			const commonData = getCommonData?.()
			const event: LogEvent = {
				type,
				identifier,
				project: projectId,
				createdTime: new Date().toISOString(),
				data: {
					...data,
					...commonData,
				},
			}
			try {
				onLog?.(event)
			} catch {
				// Suppress errors
			}

			if (!enabled || !(shouldLog?.(event) ?? true)) {
				return
			}

			queue.push(event)
			// Wait for handler if something is already queued
			if (drainScheduled) {
				return
			}

			drainQueue()
		},
		sanitize: (userData) =>
			userDataWhitelist.reduce<EventLoggerData>((obj, key) => {
				if (userData[key]) {
					obj[key] = userData[key]
				}
				return obj
			}, {}),
	}
}
